/* The factor library — gate 20. Read-only: the stylesheet has no class for a control, and that is
   deliberate. Tokens only (ADR-0007); nothing below 12px (--t-min). */

/* Provenance as one card: who published it, which edition, when it was retrieved and what it is
   licensed under are the facts a figure's receipt cites, so they sit together and above the rows. */
.facts {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(13rem, 1fr));
  gap: 0 18px;
  margin: 0 0 10px;
  padding: 12px 14px 2px;
  border: 1px solid var(--line);
  border-radius: var(--radius-chip);
  background: var(--panel-2);
  font-size: var(--t-dense);
}

/* A fact is a name over a value, and the facts of one line share the name's row and the value's row
   (alignment audit, 2026-11-03): a name that wraps to two lines used to push its own value 14px below
   its neighbours'. The first declaration is the fallback for a browser without `subgrid`. */
.fact {
  display: grid;
  gap: 2px;
  grid-template-rows: auto auto;
  grid-template-rows: subgrid;
  grid-row: span 2;
  padding-bottom: 10px;
  min-width: 0;
}

.fact dt {
  font-size: var(--t-min);
  font-weight: 600;
  color: var(--muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.fact dd {
  margin: 0;
  color: var(--ink);
  overflow-wrap: anywhere;
}

.url {
  color: var(--accent);

  /* WCAG 2.5.8 (gate 37): a link of its own in a table cell is a 24px target, not the 15px its text
     is high, so two rows of them are not two targets a fingertip cannot tell apart. */
  display: inline-flex;
  align-items: center;
  min-height: 24px;
}

/* One spend-based set (gate 20, ADR-0044): its own block inside the spend panel, set apart from the
   next by a rule rather than a second panel, so two sets read as two. */
.spendSet {
  padding-bottom: 12px;
}

.spendSet + .spendSet {
  padding-top: 14px;
  border-top: 1px solid var(--line);
}

.spendTitle {
  margin: 0 0 8px;
  font-size: var(--t-dense);
  font-weight: 600;
  color: var(--ink);
}

/* The factor search (gate 20): one native field above the sets, narrowing the rows of every set at
   once. It is the only control on the screen apart from a retry. */
.search {
  display: grid;
  gap: 4px;
  max-width: 420px;
  margin: 0 0 12px;
  font-size: var(--t-min);
  font-weight: 600;
  color: var(--ink-2);
}

.search input {
  padding: 6px 9px;
  font: inherit;
  font-size: var(--t-dense);
  font-weight: 400;
  color: var(--ink);
  background: var(--panel);
  border: 1px solid var(--line-2);
  border-radius: 6px;
  min-width: 0;
}

.search input:focus-visible,
.search select:focus-visible {
  border-color: var(--focus);
  outline: 2px solid var(--focus);
  outline-offset: 0;
}

/* The narrowing controls (gate 20): the search, a category and a CO2-only switch. They filter rows the
   screen already holds; none of them chooses an edition or edits a value. */
.filters {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 12px 20px;
}

.filters .search {
  margin: 0;
  flex: 0 1 auto;
}

.filters .search:first-child {
  flex: 1 1 260px;
}

.search select {
  padding: 6px 9px;
  font: inherit;
  font-size: var(--t-dense);
  font-weight: 400;
  color: var(--ink);
  background: var(--panel);
  border: 1px solid var(--line-2);
  border-radius: 6px;
  min-width: 0;
}

.check {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 24px;
  font-size: var(--t-dense);
  color: var(--ink);
}

.relation {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 0 0 10px;
}

.attribution {
  margin: 0 0 12px;
  padding: 8px 10px;
  border: 1px solid var(--line);
  border-left-width: 3px;
  border-radius: 8px;
  background: var(--panel-2);
  font-size: var(--t-dense);
  color: var(--ink-2);
  line-height: 1.5;
  max-width: 80ch;
}

.scroll {
  overflow-x: auto;
}

/* The factor table scrolls inside this box with its header held, so no set pushes another's
   provenance out of view. The comparison table shares `.scroll` and is deliberately not capped. */
.tableBox {
  max-height: 24rem;
  overflow-y: auto;
  border: 1px solid var(--line);
  border-radius: var(--radius-chip);
}

.tableBox .table th {
  position: sticky;
  top: 0;
  background: var(--panel);
  z-index: 1;
}

.tableBox .table th:first-child,
.tableBox .table td:first-child {
  padding-left: 10px;
}

.tableBox:focus-visible {
  outline: 2px solid var(--focus);
  outline-offset: 2px;
}

/* The honesty copy, collapsed to one line; placed across the whole grid. */
.limits {
  grid-column: 1 / -1;
  min-width: 0;
  font-size: var(--t-min);
  color: var(--ink-2);
}

.limits > summary {
  cursor: pointer;
  font-weight: 600;
}

.limits > summary:focus-visible {
  outline: 2px solid var(--focus);
  outline-offset: 2px;
}

.table {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--t-dense);
}

.caption {
  text-align: left;
  padding-bottom: 6px;
  font-size: var(--t-min);
  color: var(--muted);
}

.table th {
  text-align: left;
  font-size: var(--t-min);
  font-weight: 600;
  color: var(--muted);
  padding: 4px 10px 4px 0;
  border-bottom: 1px solid var(--line-2);
  white-space: nowrap;
}

.table td {
  padding: 5px 10px 5px 0;
  border-bottom: 1px solid var(--line);
  vertical-align: top;
  color: var(--ink-2);
}

.num {
  font-variant-numeric: lining-nums tabular-nums;
  white-space: nowrap;
  color: var(--ink);
  font-weight: 600;
}

.unit {
  font-weight: 400;
  color: var(--muted);
  font-size: var(--t-min);
}

.mono {
  font-family: var(--mono);
  font-size: var(--t-min);
  overflow-wrap: anywhere;
}

.none {
  color: var(--muted);
  font-style: italic;
}

/* The figures a factor priced (the detail page's "Used by"): a link, then what it is. */
.usedBy {
  display: flex;
  flex-direction: column;
  margin: 12px 0 0;
  padding: 0;
  list-style: none;
}

.usedBy li {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 0;
  border-bottom: 1px solid var(--line);
  min-width: 0;
}

.usedByMeta {
  font-size: var(--t-min);
  color: var(--muted);
  overflow-wrap: anywhere;
}
